import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  Plus,
  Pencil,
  Trash2,
  ArrowUpRight,
  LogOut,
  ArrowLeft,
  X,
  ShieldCheck,
} from "lucide-react";
import Footer from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "dmab_admin_token";

const FIELD =
  "w-full bg-white/[0.06] border border-white/15 rounded-xl px-4 py-3.5 text-white placeholder:text-white/40 outline-none transition-all duration-300 focus:border-[#00F0FF] focus:bg-white/[0.09] focus:ring-1 focus:ring-[#00F0FF]/40";

export default function ContentPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [showLogin, setShowLogin] = useState(false);
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const [form, setForm] = useState({ title: "", body: "" });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const isAdmin = Boolean(token);

  const fetchItems = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/content`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load content.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Validate an existing token on mount
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      });
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setAuthLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setShowLogin(false);
      setPassword("");
      toast.success("Signed in. You can now manage content.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed.");
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    resetForm();
    toast.success("Signed out.");
  };

  const resetForm = () => {
    setForm({ title: "", body: "" });
    setEditingId(null);
  };

  const saveContent = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Please add both a title and body.");
      return;
    }
    setSaving(true);
    const isEdit = Boolean(editingId);
    try {
      const url = `${API}/content${isEdit ? `/${editingId}` : ""}`;
      const headers = { Authorization: `Bearer ${token}` };
      if (isEdit) {
        await axios.put(url, form, { headers });
      } else {
        await axios.post(url, form, { headers });
      }
      toast.success(isEdit ? "Content updated." : "Content saved.");
      resetForm();
      fetchItems();
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error(err.response?.data?.detail || "Could not save.");
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteContent = async (id) => {
    if (!window.confirm("Delete this content item?")) return;
    try {
      await axios.delete(`${API}/content/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Content deleted.");
      if (editingId === id) resetForm();
      fetchItems();
    } catch (err) {
      if (err.response?.status === 401) return logout();
      toast.error("Could not delete.");
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({ title: item.title, body: item.body });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#050A15] text-white grain flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-[1100px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="content-logo" className="flex items-center">
            <img src="/dmab-logo-dark.png" alt="DMAB Solutions" className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="content-back-home"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm text-white/90 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"
            >
              <ArrowLeft size={15} /> Main site
            </Link>
            {isAdmin ? (
              <button
                onClick={logout}
                data-testid="content-logout"
                className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/15 px-4 py-2 text-sm text-white/90 hover:bg-white/10 transition-colors"
              >
                <LogOut size={15} /> Sign out
              </button>
            ) : (
              <button
                onClick={() => setShowLogin(true)}
                data-testid="content-admin-btn"
                className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/15 px-4 py-2 text-sm text-white/70 hover:text-white transition-colors"
                aria-label="Admin sign in"
              >
                <Lock size={14} /> Admin
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-[1100px] w-full mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="max-w-2xl">
          <div className="mb-4 flex items-center gap-3">
            <span className="h-px w-10 bg-[#00F0FF]" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#00F0FF]">
              DMAB Solutions
            </span>
          </div>
          <h1 className="font-display font-bold tracking-tight text-4xl md:text-5xl leading-tight">
            Insights &amp; Resources
          </h1>
          <p className="mt-4 text-[#94A3B8] font-light leading-relaxed">
            Perspectives on business consulting, process automation and
            technology enabled operations from the DMAB Solutions team.
          </p>
        </div>

        {/* Admin editor */}
        {isAdmin && (
          <form
            onSubmit={saveContent}
            data-testid="content-editor"
            className="mt-12 rounded-2xl border border-[#00F0FF]/30 bg-[#0C1322] p-6 md:p-8"
          >
            <div className="flex items-center gap-2 mb-6">
              <ShieldCheck size={18} className="text-[#00F0FF]" />
              <h2 className="font-display font-semibold text-lg">
                {editingId ? "Edit content" : "Add new content"}
              </h2>
            </div>
            <div className="space-y-4">
              <input
                data-testid="content-title-input"
                className={FIELD}
                placeholder="Title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
              <textarea
                data-testid="content-body-input"
                className={`${FIELD} resize-y min-h-[160px]`}
                placeholder="Body content…"
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              />
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="content-save-btn"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#00F0FF] px-6 py-3 font-display font-semibold text-[#050A15] hover:bg-[#33F3FF] transition-colors disabled:opacity-60"
                >
                  <Plus size={17} /> {editingId ? "Update" : "Save"}
                </button>
                {editingId && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 px-5 py-3 text-sm text-white/80 hover:text-white transition-colors"
                  >
                    <X size={15} /> Cancel
                  </button>
                )}
              </div>
            </div>
          </form>
        )}

        {/* Content list */}
        <section className="mt-12">
          {loading ? (
            <p className="text-[#94A3B8]">Loading…</p>
          ) : items.length === 0 ? (
            <div
              data-testid="content-empty"
              className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-10 text-center text-[#94A3B8]"
            >
              {isAdmin
                ? "No content yet. Use the editor above to add your first item."
                : "Content coming soon."}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5" data-testid="content-list">
              {items.map((item) =>
                isAdmin ? (
                  <article
                    key={item.id}
                    data-testid={`content-item-${item.id}`}
                    className="rounded-2xl border border-white/10 bg-[#0C1322] p-6 md:p-7"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-display font-bold text-xl md:text-2xl text-white">
                        {item.title}
                      </h3>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => startEdit(item)}
                          data-testid={`content-edit-${item.id}`}
                          className="grid place-items-center h-9 w-9 rounded-lg bg-white/5 border border-white/10 text-white/80 hover:text-[#00F0FF] hover:border-[#00F0FF]/50 transition-colors"
                          aria-label="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => deleteContent(item.id)}
                          data-testid={`content-delete-${item.id}`}
                          className="grid place-items-center h-9 w-9 rounded-lg bg-white/5 border border-white/10 text-white/80 hover:text-red-400 hover:border-red-400/50 transition-colors"
                          aria-label="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="mt-3 text-[#94A3B8] font-light leading-relaxed whitespace-pre-line">
                      {item.body}
                    </p>
                  </article>
                ) : (
                  <Link
                    key={item.id}
                    to="/"
                    data-testid={`content-item-${item.id}`}
                    className="group block rounded-2xl border border-white/10 bg-[#0C1322] p-6 md:p-8 transition-colors duration-300 hover:border-[#00F0FF]/50"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <h2 className="font-display font-bold text-xl md:text-2xl text-white group-hover:text-[#00F0FF] transition-colors">
                        {item.title}
                      </h2>
                      <ArrowUpRight
                        size={20}
                        className="text-white/30 group-hover:text-[#00F0FF] transition-colors shrink-0"
                      />
                    </div>
                    <p className="mt-3 text-[#94A3B8] font-light leading-relaxed whitespace-pre-line line-clamp-4">
                      {item.body}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#00F0FF]">
                      Visit DMAB Solutions <ArrowUpRight size={15} />
                    </span>
                  </Link>
                )
              )}
            </div>
          )}
        </section>
      </main>

      <Footer />

      {/* Login modal */}
      {showLogin && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm px-5"
          onClick={() => setShowLogin(false)}
          data-testid="login-modal"
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleLogin}
            className="w-full max-w-sm rounded-2xl border border-white/15 bg-[#0C1322] p-7"
          >
            <div className="flex items-center gap-2 mb-5">
              <Lock size={18} className="text-[#00F0FF]" />
              <h2 className="font-display font-semibold text-lg">Admin sign in</h2>
            </div>
            <input
              type="password"
              autoFocus
              data-testid="login-password-input"
              className={FIELD}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="submit"
              disabled={authLoading}
              data-testid="login-submit"
              className="mt-4 w-full rounded-xl bg-[#00F0FF] px-6 py-3 font-display font-semibold text-[#050A15] hover:bg-[#33F3FF] transition-colors disabled:opacity-60"
            >
              {authLoading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
